import * as EmailCenterSelectors from '@/selectors/emailCenterSelectors';
import { useMemo } from 'react';
import { useSelector } from 'umi';
import {
  anyWording,
  resolveTranslations,
  translationKey,
} from '../translationCatalog';
import { TranslationEntry } from './variableOptions';

/**
 * The partner's saved texts, ready for a `{{` list: every key in the catalog with the
 * wording it currently has for one language.
 *
 * Read from the store rather than threaded through props. The three surfaces that offer
 * the list — the subject/pretext fields, the rich-text blocks and the Twig source view —
 * sit at the end of three different prop chains (the template drawer, the inspector, the
 * fragment drawer), and the catalog is partner-wide: passing it down each chain would be
 * five components carrying a value none of them has an opinion about, and the visual
 * editor's `translations` prop is the *resolved* map for the preview language, which has
 * already dropped every key this language has no words for.
 *
 * `language` is which wording to show beside each key, and strictly that one: a key with
 * no words for it shows none, because a fallback would claim a translation that does not
 * exist. Omitted — the Twig source editor has no language of its own — each key shows
 * whatever wording it has, so the rows still read as sentences.
 */
export function useTranslationEntries(language?: string): TranslationEntry[] {
  const translations = useSelector(EmailCenterSelectors.selectTranslations);

  return useMemo(() => {
    const values = language
      ? resolveTranslations(translations, language)
      : null;
    return translations
      .map((row) => {
        const key = translationKey(row);
        return { key, text: values ? values[key] : anyWording(row) };
      })
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [translations, language]);
}
